import { useSyncExternalStore } from "react";

/**
 * 데이터셋별 "저장하지 않은 에피소드 검토" 핸들.
 *
 * 편집은 리뷰 독이 소유하고, 창을 닫는 버튼은 창 셸이 소유한다. 둘은 부모-자식이 아니라 형제다.
 * 그래서 모듈 스토어로 연결한다(`datasetDownloadStore` 와 같은 구조). 이탈 가드가 "버리고
 * 나갈까요"를 물으려면 dirty 여부만으로는 부족하다. 그래서 되돌리기 명령도 함께 담는다. 저장
 * 명령은 담지 않는다. 가드는 저장하지 않고, 사용자를 저장 버튼으로 돌려보낼 뿐이다.
 *
 * 에피소드 전환 가드는 `nav.setChangeGuard` 가 따로 처리한다. 그 가드는 "이 전환을 보류한다"는
 * 제어가 필요하다. 이 스토어는 셸이 나가기 전에 확인만 하면 된다.
 *
 * 창을 여러 개 띄울 수 있으므로 데이터셋별로 나눈다.
 */
export type PendingEpisodeReview = {
  // 편집을 저장값으로 되돌린다.
  revert: () => void;
};

const pending = new Map<string, PendingEpisodeReview>();
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) {
    listener();
  }
}

export const episodeReviewDirtyStore = {
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  isDirty(datasetUuid: string): boolean {
    return pending.has(datasetUuid);
  },
  get(datasetUuid: string): PendingEpisodeReview | null {
    return pending.get(datasetUuid) ?? null;
  },
  /** 미저장 편집이 생기면 명령과 함께 등록하고, 저장·원복·언마운트로 사라지면 null 로 지운다. */
  set(datasetUuid: string, handle: PendingEpisodeReview | null): void {
    if (handle === null) {
      if (!pending.delete(datasetUuid)) {
        return;
      }
      notify();
      return;
    }
    // 명령은 매 렌더 새로 만들어진다. 그래서 dirty 상태가 이미 등록되어 있으면 알리지 않고 교체한다.
    // 알리면 구독자가 매 렌더 다시 렌더되고, 그 렌더가 다시 등록을 호출한다.
    const had = pending.has(datasetUuid);
    pending.set(datasetUuid, handle);
    if (!had) {
      notify();
    }
  },
};

/** 이 데이터셋에 저장하지 않은 검토가 남아 있는가. 창 이탈 가드가 구독한다. */
export function useEpisodeReviewDirty(datasetUuid: string): boolean {
  return useSyncExternalStore(episodeReviewDirtyStore.subscribe, () =>
    episodeReviewDirtyStore.isDirty(datasetUuid),
  );
}
